import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api.js';

/** The handful of primitives five tables need, and nothing else. */

/**
 * Run an async read, and say which of the three states it is in.
 *
 * Keeps the previous value while refetching, so a table does not blink to empty every time the
 * screen reloads it — the same behaviour `apps/web`'s `useAsync` has, for the same reason.
 */
export function useAsync<T>(
  read: () => Promise<T>,
  deps: readonly unknown[],
): { data: T | null; error: string | null; loading: boolean; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    read()
      .then((value) => {
        if (!live) return;
        setData(value);
        setError(null);
      })
      .catch((failure: unknown) => {
        if (!live) return;
        setError(failure instanceof ApiError ? failure.message : String(failure));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload: useCallback(() => setNonce((n) => n + 1), []) };
}

/**
 * The thing selected *within* a section, from the fragment — `#accounts/01JD…`.
 *
 * Sections are addressable on purpose (§5): "look at this team" is a thing to paste into a
 * message, and it has to land on the row it names rather than on the section's own index.
 */
export function useSelection(section: string): [string | null, (next: string | null) => void] {
  const read = () => {
    const [head, ...rest] = window.location.hash.replace(/^#/, '').split('/');
    return head === section && rest.length > 0 ? rest.join('/') : null;
  };
  const [selected, setSelected] = useState<string | null>(read);

  useEffect(() => {
    const onChange = () => setSelected(read());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  return [
    selected,
    (next) => {
      window.location.hash = next === null ? section : `${section}/${next}`;
      setSelected(next);
    },
  ];
}

/** `1h 20m`, `4m 12s`, `—`. Durations here are sandbox time and playtime, never anything smaller. */
export function duration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  // A zero remainder is noise: "1h 0m" and "41m 0s" read as precision that is not there.
  if (minutes < 60) return seconds % 60 === 0 ? `${minutes}m` : `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return minutes % 60 === 0 ? `${hours}h` : `${hours}h ${minutes % 60}m`;
}

/*
 * ------------------------------------------------------------------------------------ time
 *
 * Every timestamp on every screen is **the reader's local time**, and none of it is UTC.
 *
 * The server sends epoch milliseconds and nothing else — there is no timezone anywhere on the wire
 * and there must not be one, because the moment is the fact and the rendering is the reader's. What
 * this console used to do was `toISOString()`, which is UTC wearing no marking at all: an operator
 * in Prague reading "14:32" over a sandbox that started at 16:32 their afternoon has been told
 * something false, and the only clue was two hours of quiet disagreement with their own clock.
 *
 * So the formatting is done from the local getters rather than from `toLocaleString`, for two
 * reasons: the layout stays `YYYY-MM-DD HH:MM`, which is the one ordering that reads correctly for
 * an audience whose locale nobody has asked about; and it is stable, so a screenshot taken in CI
 * does not depend on which locale data Chrome shipped with.
 *
 * `zoneName()` is what stops the format from being ambiguous in the other direction. A bare local
 * time is only honest if the reader knows it is local, so the header says which zone the page is
 * being read in, once, and every `<Time>` carries the full moment in its tooltip.
 */

const pad = (value: number) => String(value).padStart(2, '0');

/** `2026-08-25`, in the reader's zone. Not `toISOString().slice(0, 10)`, which is the UTC day. */
export function localDay(at: number): string {
  const date = new Date(at);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `2026-08-25 16:32`, in the reader's zone. */
export function when(at: number | null | undefined): string {
  if (at === null || at === undefined) return '—';
  const date = new Date(at);
  return `${localDay(at)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** The zone this browser is in — `Europe/Prague`, or `UTC+02:00` where there is no name. */
export function zoneName(): string {
  const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (resolved) return resolved;
  // `getTimezoneOffset` is minutes *behind* UTC, so the sign is inverted on the way out.
  const minutes = -new Date().getTimezoneOffset();
  const sign = minutes < 0 ? '-' : '+';
  return `UTC${sign}${pad(Math.floor(Math.abs(minutes) / 60))}:${pad(Math.abs(minutes) % 60)}`;
}

/**
 * A moment, rendered locally, with the whole of it in the tooltip.
 *
 * `<time dateTime>` carries the unambiguous ISO instant for anything reading the DOM, the text is
 * the short local form the table wants, and the `title` is the long one — seconds and zone — for
 * the case where two rows a minute apart need telling apart.
 */
export function Time({ at }: { at: number | null | undefined }) {
  if (at === null || at === undefined) return <>—</>;
  const date = new Date(at);
  return (
    <time dateTime={date.toISOString()} title={`${date.toString()} (${zoneName()})`}>
      {when(at)}
    </time>
  );
}

export function Panel({ title, children, actions }: {
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <section className="panel">
      <header className="panel-head">
        <h2>{title}</h2>
        {actions ? <div className="row">{actions}</div> : null}
      </header>
      {children}
    </section>
  );
}

export function Async<T>({ state, children, empty }: {
  state: { data: T | null; error: string | null; loading: boolean };
  children: (data: T) => React.ReactNode;
  empty?: string;
}) {
  if (state.error) return <p className="error">{state.error}</p>;
  if (state.data === null) return <p className="muted">{state.loading ? 'Loading…' : (empty ?? 'Nothing.')}</p>;
  return <>{children(state.data)}</>;
}

/** How many more rows each "Show more" asks for — the list routes' own default page. */
const LIST_PAGE = 50;

/**
 * How long a list has been asked to be (`?limit=`, `apps/admin/src/list-limit.ts`), and the press
 * that lengthens it. `listKey` names the search and filters it was pressed on: another one is
 * another list, which starts again at one page without a second read to get back there.
 */
export function useListLength(listKey: string): [number, () => void] {
  const [more, setMore] = useState({ listKey, shown: LIST_PAGE });
  const shown = more.listKey === listKey ? more.shown : LIST_PAGE;
  return [shown, () => setMore({ listKey, shown: shown + LIST_PAGE })];
}

/**
 * The line under a list the route cut short: how many of how many, and "Show more". Nothing when
 * the list is whole, so a list that fits says nothing about it.
 */
export function ListMore({ state, shown, noun, onMore }: {
  state: { data: { items: unknown[]; limit: number; total: number } | null; loading: boolean };
  shown: number;
  noun: string;
  onMore: () => void;
}) {
  const data = state.data;
  if (!data || data.items.length >= data.total) return null;
  return (
    <div className="row">
      <span className="muted">
        {data.items.length} of {data.total} {noun}.
      </span>
      {/* Hidden while a longer read is on its way, and once the route stops lengthening the list:
          past its cap, a search is the way to a row. */}
      {data.limit === shown ? (
        <button onClick={onMore}>Show more</button>
      ) : state.loading ? null : (
        <span className="muted">Search to find the rest.</span>
      )}
    </div>
  );
}

/**
 * A value that follows what somebody is typing, a beat behind.
 *
 * Every filter on every list here is applied by the **server** — a filter the browser applied to
 * the fifty rows a search already returned would answer "no paid teams" when what it meant was
 * "none in this page". That makes each keystroke a request, so the text inputs debounce and the
 * selects do not: a dropdown is one decision and should take effect the instant it is made.
 */
export function useDebounced<T>(value: T, ms = 250): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/**
 * A `<select>` with a label and an "any" option, which is every filter on every list here.
 *
 * The empty string is *no opinion* and is what the routes read as "do not add this clause", so a
 * filter reset is the same value a page loads with and there is no third state to get wrong.
 */
export function Filter({ label, value, onChange, options }: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: readonly { value: string; label: string }[];
}) {
  return (
    <label className="filter">
      <span className="muted small">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * The detail view, in a real modal.
 *
 * A native `<dialog>` opened with `showModal()`, and not a positioned `<div>` with a hand-rolled
 * overlay. Everything a modal has to get right — Escape, the top layer above every stacking
 * context, inert content behind it, focus moved in and restored on close — the element already
 * does, correctly, and a re-implementation of any one of them is a re-implementation of all of
 * them a few weeks later.
 *
 * Two things are wired by hand because they are policy rather than mechanics:
 *
 *  - **`cancel`** — Escape closes the dialog natively, and the caller has to hear about it, or the
 *    fragment would still name a row the screen has stopped showing.
 *  - **The backdrop** — a click on it targets the dialog itself, since the backdrop is a
 *    pseudo-element and never an event target. Anything inside the panel targets the panel, so the
 *    identity check is the whole test.
 *
 * `full` makes it a page of its own over the list — for an account, which has more sections than a
 * team or a game — and `subhead` is what stays put above the scrolling body: a line about the
 * subject and the tabs between its sections.
 */
export function Modal({ title, onClose, actions, children, full, subhead }: {
  title: string;
  onClose: () => void;
  actions?: React.ReactNode;
  children: React.ReactNode;
  full?: boolean;
  subhead?: React.ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => dialog?.close();
  }, []);

  return (
    <dialog
      ref={ref}
      className={full ? 'modal modal-full' : 'modal'}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
    >
      <header className="modal-head">
        <h2>{title}</h2>
        <span className="grow" />
        {actions}
        <button onClick={onClose} aria-label="Close">
          Close
        </button>
      </header>
      {subhead ? <div className="modal-subhead">{subhead}</div> : null}
      <div className="modal-body">{children}</div>
    </dialog>
  );
}
